import React, { useEffect, useState } from 'react'
import { getStoreListings } from '../../firebase/shopify'

/**
 * One store's listings as the marketplace sees them: the price a shopper
 * actually pays (Shopify price times the store's multiplier) and whether the
 * owner has switched each one off.
 *
 * Read-only. Visibility is the store owner's lever, not the admin's -- an
 * admin who wants a whole store off the marketplace blocks it instead.
 */
const DialogStoreListings = ({ shop, open, onClose }) => {
    const [state, setState] = useState({ loading: true, error: '', data: null })

    useEffect(() => {
        if (!open || !shop) return

        setState({ loading: true, error: '', data: null })
        getStoreListings(shop)
            .then((data) => setState({ loading: false, error: '', data }))
            .catch((error) => setState({ loading: false, error: error.message || 'Failed to load listings', data: null }))
    }, [open, shop])

    if (!open) return null

    const listings = state.data?.listings || []

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4">
            <div className="bg-[#1A1A1A] border border-[#3A3A3A] rounded-[8px] w-full max-w-4xl max-h-[85vh] flex flex-col">
                <div className="flex items-start justify-between p-5 border-b border-[#3A3A3A]">
                    <div>
                        <h2 className="text-white text-[20px]">{shop}</h2>
                        {state.data && (
                            <p className="text-[#a0a0a0] text-sm mt-1">
                                {listings.length} listings &middot; price multiplier {state.data.price_multiplier}
                                {state.data.blocked && <span className="text-red-400 ml-2">&middot; store blocked</span>}
                            </p>
                        )}
                    </div>
                    <button className="text-[#a0a0a0] hover:text-white text-xl leading-none" onClick={onClose}>&times;</button>
                </div>

                <div className="overflow-auto p-5">
                    {state.loading && <p className="text-sm text-[#a0a0a0]">Loading listings...</p>}
                    {state.error && <p className="text-sm text-red-400">{state.error}</p>}

                    {!state.loading && !state.error && listings.length === 0 && (
                        <p className="text-sm text-[#a0a0a0]">This store has no synced listings.</p>
                    )}

                    {listings.length > 0 && (
                        <table className="w-full">
                            <thead>
                                <tr className="bg-[#2E2E2E] text-sm text-[#717171]">
                                    <th className="py-3 px-3 text-left">Product</th>
                                    <th className="py-3 px-3 text-left">Shopify price</th>
                                    <th className="py-3 px-3 text-left">Marketplace price</th>
                                    <th className="py-3 px-3 text-left">Inventory</th>
                                    <th className="py-3 px-3 text-left">Visibility</th>
                                </tr>
                            </thead>
                            <tbody className="text-white text-sm">
                                {listings.map((listing) => (
                                    <tr key={listing.id} className={`h-[60px] hover:bg-[#2F2F2F] ${listing.hidden ? 'opacity-50' : ''}`}>
                                        <td className="py-3 px-3">
                                            <div className="flex items-center gap-3">
                                                <img
                                                    src={listing.image || '/assets/images/image_item_default.png'}
                                                    alt=""
                                                    className="w-[40px] h-[40px] object-cover shrink-0"
                                                />
                                                <span className="uppercase">{listing.title}</span>
                                            </div>
                                        </td>
                                        <td className="py-3 px-3 text-[#a0a0a0]">${Number(listing.base_price || 0).toFixed(2)}</td>
                                        <td className="py-3 px-3">${Number(listing.price || 0).toFixed(2)}</td>
                                        <td className="py-3 px-3">
                                            {listing.quantity > 0
                                                ? listing.quantity
                                                : <span className="text-yellow-400">Out of stock</span>}
                                        </td>
                                        <td className="py-3 px-3">
                                            {listing.hidden
                                                ? <span className="text-[#717171] uppercase text-xs">Hidden</span>
                                                : <span className="text-green-400 uppercase text-xs">Listed</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    )
}

export default DialogStoreListings
